'use client'

import { forwardRef, useImperativeHandle, useRef } from 'react';
import { motion } from 'motion/react';
import {
  Toaster as SonnerToaster,
  toast as sonnerToast,
} from 'sonner';
import {
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'success' | 'error' | 'warning' | 'info';
type Position =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

interface ActionButton {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'ghost';
}

interface ToasterProps {
  title?: string;
  message: string;
  variant?: Variant;
  duration?: number;
  position?: Position;
  actions?: ActionButton;
  onDismiss?: () => void;
  highlightTitle?: boolean;
}

export interface ToasterRef {
  show: (props: ToasterProps) => void;
}

const variantStyles: Record<Variant, string> = {
  default: 'bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] border-gray-200 dark:border-[#8a2be2]/30 text-gray-900 dark:text-[#e0e1dd]',
  success: 'bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] border-green-600/50 dark:border-green-500/50',
  error: 'bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] border-red-600/50 dark:border-red-500/50',
  warning: 'bg-white dark:bg-[#1b263b] border-amber-600/50 dark:border-amber-500/50',
  info: 'bg-white dark:bg-[#1b263b] border-blue-600/50 dark:border-blue-500/50',
};

const titleColor: Record<Variant, string> = {
  default: 'text-gray-900 dark:text-[#e0e1dd]',
  success: 'text-green-600 dark:text-green-400',
  error: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
  info: 'text-blue-600 dark:text-blue-400',
};

const iconColor: Record<Variant, string> = {
  default: 'text-gray-600 dark:text-[#778da9]',
  success: 'text-green-600 dark:text-green-400',
  error: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
  info: 'text-blue-600 dark:text-blue-400',
};

const variantIcons: Record<Variant, React.ComponentType<{ className?: string }>> = {
  default: Info,
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const toastAnimation = {
  initial: { opacity: 0, y: -20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -20, scale: 0.95 },
};

const Toaster = forwardRef<ToasterRef, { defaultPosition?: Position }>(
  ({ defaultPosition = 'top-center' }, ref) => {
    const toastReference = useRef<ReturnType<typeof sonnerToast.custom> | null>(null);

    useImperativeHandle(ref, () => ({
      show({
        title,
        message,
        variant = 'default',
        duration = 4000,
        position = defaultPosition,
        actions,
        onDismiss,
        highlightTitle,
      }) {
        const Icon = variantIcons[variant];

        toastReference.current = sonnerToast.custom(
          (toastId) => (
            <motion.div
              variants={toastAnimation}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={cn(
                'flex items-center justify-between w-full max-w-xs p-3 rounded-xl border shadow-lg backdrop-blur-sm',
                variantStyles[variant]
              )}
            >
              <div className="flex items-start gap-2 flex-1">
                <Icon className={cn('h-4 w-4 mt-0.5 flex-shrink-0', iconColor[variant])} />
                <div className="space-y-0.5 flex-1">
                  {title && (
                    <h3
                      className={cn(
                        'text-xs font-semibold leading-none font-ubuntu',
                        highlightTitle ? titleColor['success'] : titleColor[variant]
                      )}
                    >
                      {title}
                    </h3>
                  )}
                  <p className="text-xs text-gray-600 dark:text-[#778da9] font-montserrat leading-relaxed">{message}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 ml-2">
                {actions?.label && (
                  <Button
                    type={actions.variant === 'outline' ? 'secondary' : actions.variant === 'ghost' ? 'tertiary' : 'primary'}
                    size="small"
                    onClick={() => {
                      actions.onClick();
                      sonnerToast.dismiss(toastId);
                    }}
                    className={cn(
                      'cursor-pointer text-xs',
                      variant === 'success'
                        ? 'text-green-600 border-green-600 hover:bg-green-600/10 dark:hover:bg-green-400/20'
                        : variant === 'error'
                        ? 'text-red-600 border-red-600 hover:bg-red-600/10 dark:hover:bg-red-400/20'
                        : variant === 'warning'
                        ? 'text-amber-600 border-amber-600 hover:bg-amber-600/10 dark:hover:bg-amber-400/20'
                        : variant === 'info'
                        ? 'text-blue-600 border-blue-600 hover:bg-blue-600/10 dark:hover:bg-blue-400/20'
                        : 'text-gray-600 border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    )}
                  >
                    {actions.label}
                  </Button>
                )}

                <button
                  onClick={() => {
                    sonnerToast.dismiss(toastId);
                    onDismiss?.();
                  }}
                  className="rounded-full p-1 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#415a77]"
                  aria-label="Dismiss notification"
                >
                  <X className="h-3 w-3 text-gray-500 dark:text-[#778da9]" />
                </button>
              </div>
            </motion.div>
          ),
          { duration, position }
        );
      },
    }));

    return (
      <SonnerToaster
        position={defaultPosition}
        toastOptions={{ unstyled: true, className: 'flex justify-center' }}
      />
    );
  }
);

Toaster.displayName = "Toaster";

export default Toaster;

